import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  IconButton,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import LogoutIcon from '@mui/icons-material/Logout';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ScreenShareIcon from '@mui/icons-material/ScreenShare';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
  DeviceView,
  clearToken,
  createDesktopSession,
  endDesktopSession,
  listDevices,
  deleteDevice,
  pushDeviceUpdate,
  revokeDevice,
  updateDevice,
} from './api';
import { relativeTime, sshCommandFor } from './format';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { DesktopViewer } from './desktop/DesktopViewer';
import { DownloadsTable } from './DownloadsTable';
import { FileBrowserDialog } from './FileBrowserDialog';
import { EnrollDialog } from './EnrollDialog';
import { PackDownloadsTable } from './PackDownloadsTable';
import { SessionsTable } from './SessionsTable';
import { TunnelsTable } from './TunnelsTable';

/** Live enough to trust: the spec's staleness budget is 30s (AC5). */
const POLL_MS = 10_000;

/** Why Screen cannot be used on this box right now, or null when it can. */
function screenBlockedReason(device: DeviceView): string | null {
  if (device.status === 'revoked') return 'This device is revoked.';
  if (!device.online) return 'The box is offline.';
  if (!device.desktopCapable) {
    return 'This agent does not support screen sharing yet — upgrade it.';
  }
  if (device.desktopBusy) return 'A screen session is already open on this box.';
  return null;
}

/** Why the Update push is unavailable right now, or null when it can be sent. */
function updateBlockedReason(device: DeviceView): string | null {
  if (!device.online) {
    return 'The box is offline — an autoupdating agent picks this up by itself when it reconnects.';
  }
  if (!device.canPushUpdate) {
    return 'This agent predates remote update — upgrade it once by hand (update.sh / Update-DikonekTunnelAgent); every later update is one click.';
  }
  return null;
}

/** Why the file browser is unavailable on this box, or null when it can open. */
function filesBlockedReason(device: DeviceView): string | null {
  if (device.status === 'revoked') return 'This device is revoked.';
  if (!device.online) return 'The box is offline.';
  if (!device.fileBrowseCapable) {
    // Opt-in and default OFF, so "not enabled" is the common case here — say
    // which switch, on which file, rather than implying the box is broken.
    return 'The file browser is off on this box. Set "allowFileBrowse": true in its agent.json (or upgrade an agent older than 0.9.0).';
  }
  return null;
}

function StatusChip({ device }: { device: DeviceView }) {
  if (device.status === 'revoked') {
    return <Chip label="revoked" size="small" color="error" variant="outlined" />;
  }
  return device.online ? (
    <Chip label="online" size="small" color="success" />
  ) : (
    <Chip label="offline" size="small" variant="outlined" />
  );
}

type PageTab = 'devices' | 'sessions' | 'tunnels' | 'packs' | 'downloads';

export function DevicesPage({
  onLogout,
  forcePasswordChange = false,
}: {
  onLogout: () => void;
  /** True right after a login that used the well-known seeded password. */
  forcePasswordChange?: boolean;
}) {
  const queryClient = useQueryClient();
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [tab, setTab] = useState<PageTab>('devices');
  const [passwordOpen, setPasswordOpen] = useState(forcePasswordChange);
  const [passwordForced, setPasswordForced] = useState(forcePasswordChange);
  const [screen, setScreen] = useState<{
    sessionId: string;
    deviceName: string;
    deviceId: string;
    fileDropCapable: boolean;
  } | null>(null);
  /** Device name an update push was just sent to — shows the "watch it come back" note. */
  const [updateSent, setUpdateSent] = useState<string | null>(null);
  /** Which box the file browser is open on; null when closed. */
  const [browsing, setBrowsing] = useState<{ id: string; name: string } | null>(null);

  const devices = useQuery({
    queryKey: ['devices'],
    queryFn: listDevices,
    refetchInterval: POLL_MS,
  });

  const revoke = useMutation({
    mutationFn: revokeDevice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['devices'] }),
  });

  const remove = useMutation({
    mutationFn: deleteDevice,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['devices'] }),
  });

  const editUser = useMutation({
    mutationFn: (v: { id: string; sshUser: string }) =>
      updateDevice(v.id, { sshUser: v.sshUser }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['devices'] }),
  });

  const openScreen = useMutation({
    mutationFn: (device: DeviceView) => createDesktopSession(device.id),
    onSuccess: (session, device) => {
      setScreen({
        sessionId: session.sessionId,
        deviceName: session.deviceName,
        deviceId: device.id,
        fileDropCapable: device.fileDropCapable,
      });
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  const pushUpdate = useMutation({
    mutationFn: (device: DeviceView) => pushDeviceUpdate(device.id),
    onSuccess: (result) => {
      setUpdateSent(result.name);
      void queryClient.invalidateQueries({ queryKey: ['devices'] });
    },
  });

  function closeScreen() {
    const open = screen;
    setScreen(null);
    if (!open) return;
    // The broker also ends the session when the viewer socket closes, so this
    // only makes the row close promptly — a failed DELETE leaks nothing.
    void endDesktopSession(open.sessionId).catch(() => undefined);
    void queryClient.invalidateQueries({ queryKey: ['devices'] });
  }

  function promptSshUser(device: DeviceView) {
    const next = window.prompt(
      `SSH login for ${device.name} (used by "dkn ssh ${device.name} --user …"):`,
      device.sshUser,
    );
    const trimmed = next?.trim();
    if (trimmed && trimmed !== device.sshUser) editUser.mutate({ id: device.id, sshUser: trimmed });
  }

  function logout() {
    clearToken();
    onLogout();
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Box
            component="img"
            src="/dikonek-symbol.png"
            alt=""
            // Decorative: the wordmark right beside it already says "Dikonek
            // Tunnel", so an alt here would just make screen readers say it
            // twice.
            aria-hidden="true"
            sx={{ height: 30, width: 'auto', mr: 1.5, display: 'block' }}
          />
          <Typography variant="h6" fontWeight={600} sx={{ flexGrow: 1 }}>
            Dikonek Tunnel
          </Typography>
          <Button startIcon={<AddIcon />} onClick={() => setEnrollOpen(true)} sx={{ mr: 1 }}>
            Enroll device
          </Button>
          <Tooltip title="Public install page — open it on the machine you're setting up">
            <Button
              startIcon={<DownloadIcon />}
              href="/download"
              target="_blank"
              rel="noopener"
              sx={{ mr: 1 }}
            >
              Install page
            </Button>
          </Tooltip>
          <Tooltip title="Change password">
            <IconButton onClick={() => setPasswordOpen(true)} aria-label="Change password">
              <VpnKeyIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={logout} aria-label="Sign out">
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Stack spacing={2}>
          {/* The dashboard is deliberately blind: it lists boxes and hands you a
              command. Connecting happens in your own terminal, so the broker
              never sees a session (D5). */}
          <Alert severity="info" variant="outlined">
            Connect from your terminal with <code>dkn ssh &lt;device&gt;</code>. Sessions are
            end-to-end between your laptop and the box — the broker relays ciphertext only.
          </Alert>

          {/* No licence tabs: licensing moved to license.dikonek.com on
              2026-10-23. Pack downloads stay — the MES add-on pack gate is
              still served by this broker. Downloads lists what the public
              /downloads/ folder and the agent endpoints publish. */}
          <Tabs value={tab} onChange={(_e, v: PageTab) => setTab(v)}>
            <Tab label="Devices" value="devices" />
            <Tab label="Sessions" value="sessions" />
            <Tab label="Tunnels" value="tunnels" />
            <Tab label="Pack downloads" value="packs" />
            <Tab label="Downloads" value="downloads" />
          </Tabs>

          {tab === 'sessions' && <SessionsTable />}
          {tab === 'tunnels' && <TunnelsTable />}
          {tab === 'packs' && <PackDownloadsTable />}
          {tab === 'downloads' && <DownloadsTable />}

          {tab === 'devices' && revoke.isError && (
            <Alert severity="error">
              {revoke.error instanceof Error ? revoke.error.message : 'revoke failed'}
            </Alert>
          )}

          {tab === 'devices' && openScreen.isError && (
            <Alert severity="error">
              {openScreen.error instanceof Error
                ? openScreen.error.message
                : 'could not open the screen session'}
            </Alert>
          )}

          {tab === 'devices' && pushUpdate.isError && (
            <Alert severity="error">
              {pushUpdate.error instanceof Error ? pushUpdate.error.message : 'update push failed'}
            </Alert>
          )}

          {tab === 'devices' && updateSent && (
            <Alert severity="info" onClose={() => setUpdateSent(null)}>
              Update signal sent to <strong>{updateSent}</strong> — it downloads, verifies and
              swaps its agent, then restarts. Expect a sub-minute offline blip; the Agent column
              shows the new version when it reconnects.
            </Alert>
          )}

          {tab === 'devices' && devices.isError && (
            <Alert severity="error">
              {devices.error instanceof Error ? devices.error.message : 'could not load devices'}
            </Alert>
          )}

          {tab === 'devices' && (
          <Paper elevation={1}>
            {devices.isLoading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', p: 6 }}>
                <CircularProgress />
              </Box>
            ) : devices.data && devices.data.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography color="text.secondary" gutterBottom>
                  No devices enrolled yet.
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEnrollOpen(true)}>
                  Enroll your first device
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>OS</TableCell>
                      <TableCell>Agent</TableCell>
                      <TableCell>Last seen</TableCell>
                      <TableCell>Connect</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.data?.map((device) => (
                      <TableRow key={device.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{device.name}</TableCell>
                        <TableCell>
                          <StatusChip device={device} />
                        </TableCell>
                        <TableCell>{device.customer ?? '—'}</TableCell>
                        <TableCell>{device.os}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{device.agentVersion ?? '—'}</span>
                            {device.updateAvailable && device.latestVersion && (
                              <Tooltip
                                title={
                                  updateBlockedReason(device) ??
                                  `Update to ${device.latestVersion} now — the box swaps its agent and restarts in under a minute`
                                }
                              >
                                <span>
                                  <Button
                                    size="small"
                                    color="warning"
                                    startIcon={<SystemUpdateAltIcon fontSize="small" />}
                                    disabled={
                                      updateBlockedReason(device) !== null || pushUpdate.isPending
                                    }
                                    aria-label={`Update ${device.name}`}
                                    onClick={() => pushUpdate.mutate(device)}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    {device.latestVersion}
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{relativeTime(device.lastSeenAt)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Tooltip
                              title={
                                copied === device.id ? 'Copied' : 'Copy the command, run it locally'
                              }
                            >
                              <Button
                                size="small"
                                startIcon={<ContentCopyIcon fontSize="small" />}
                                onClick={() => {
                                  void navigator.clipboard?.writeText(
                                    sshCommandFor(device.name, device.sshUser),
                                  );
                                  setCopied(device.id);
                                }}
                                sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                              >
                                {sshCommandFor(device.name, device.sshUser)}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Change the SSH login">
                              <IconButton
                                size="small"
                                aria-label={`Edit SSH user for ${device.name}`}
                                onClick={() => promptSshUser(device)}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                screenBlockedReason(device) ??
                                'Watch and control this screen from the browser'
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  startIcon={<ScreenShareIcon fontSize="small" />}
                                  disabled={
                                    screenBlockedReason(device) !== null || openScreen.isPending
                                  }
                                  aria-label={`Screen ${device.name}`}
                                  onClick={() => openScreen.mutate(device)}
                                  sx={{ textTransform: 'none', ml: 1 }}
                                >
                                  Screen
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                filesBlockedReason(device) ??
                                'Browse this box, download files, upload into a folder'
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  startIcon={<FolderOpenIcon fontSize="small" />}
                                  disabled={filesBlockedReason(device) !== null}
                                  aria-label={`Files ${device.name}`}
                                  onClick={() => setBrowsing({ id: device.id, name: device.name })}
                                  sx={{ textTransform: 'none', ml: 1 }}
                                >
                                  Files
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {device.status !== 'revoked' && (
                            <Tooltip title="Revoke — disconnects the agent and refuses its key">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={revoke.isPending}
                                  aria-label={`Revoke ${device.name}`}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Revoke ${device.name}? Its agent will be disconnected and cannot reconnect. Break-glass SSH still works.`,
                                      )
                                    ) {
                                      revoke.mutate(device.id);
                                    }
                                  }}
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {/* Only once it is revoked. Deleting is the second of
                              two deliberate steps, and it takes the device's
                              session history with it (ON DELETE CASCADE), so the
                              confirm says how much. */}
                          {device.status === 'revoked' && (
                            <Tooltip title="Delete permanently — frees the name and removes its session history">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={remove.isPending}
                                  aria-label={`Delete ${device.name}`}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        [
                                          `Delete ${device.name} for good?`,
                                          'This frees the name for a fresh enrol and removes its ' +
                                            'break-glass key. Its session history is deleted too, ' +
                                            'and none of it can be undone.',
                                          // The half people miss: this clears the broker only.
                                          'This only clears the broker. The agent stays installed ' +
                                            'on the machine and keeps trying to connect — uninstall ' +
                                            'it on the box too (see the Install page).',
                                        ].join('\n\n'),
                                      )
                                    ) {
                                      remove.mutate(device.id);
                                    }
                                  }}
                                >
                                  <DeleteForeverIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
          )}
        </Stack>
      </Container>

      <EnrollDialog
        open={enrollOpen}
        onClose={() => {
          setEnrollOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['devices'] });
        }}
      />
      <Dialog
        open={screen !== null}
        onClose={closeScreen}
        fullScreen
        slotProps={{ paper: { sx: { bgcolor: 'grey.900' } } }}
      >
        {screen && (
          <DesktopViewer
            sessionId={screen.sessionId}
            deviceName={screen.deviceName}
            deviceId={screen.deviceId}
            fileDropCapable={screen.fileDropCapable}
            onClose={closeScreen}
          />
        )}
      </Dialog>
      {browsing && (
        <FileBrowserDialog
          open
          deviceId={browsing.id}
          deviceName={browsing.name}
          onClose={() => setBrowsing(null)}
        />
      )}
      <ChangePasswordDialog
        open={passwordOpen}
        forced={passwordForced}
        onClose={() => {
          setPasswordOpen(false);
          setPasswordForced(false);
        }}
      />
    </Box>
  );
}
